import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Plus, Trash2, Upload } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Field, Input, Textarea } from '@/components/ui/Field';
import { Spinner, EmptyState } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/ToastContext';
import { supabase } from '@/lib/supabaseClient';
import { uploadPageImage, slugify } from '@/lib/customPageApi';
import type { IngredientDetail, ProductReview, FaqItem, ClinicalResult, ProductDesignV2 } from '@/types/product';
import { useSiteContentValue } from '@/hooks/useSiteContentValue';
import DesignV2Editor from './DesignV2Editor';
import { rxkModuleIcon, rxkModuleTitle, rxkNavGroups } from '../../nav';

interface CoreProductInfo {
  sku: string;
  name_en: string;
  step_label: string;
  volume_ml: string | null;
  msrp_bdt: string;
  status: string;
}

interface FieldDef<T> {
  key: keyof T;
  label: string;
  type?: 'text' | 'textarea' | 'number' | 'checkbox';
  placeholder?: string;
}

function RepeaterEditor<T extends Record<string, unknown>>({
  items,
  onChange,
  fields,
  empty,
  addLabel,
}: {
  items: T[];
  onChange: (items: T[]) => void;
  fields: FieldDef<T>[];
  empty: T;
  addLabel: string;
}) {
  const updateItem = (index: number, key: keyof T, value: unknown) => {
    onChange(items.map((it, i) => (i === index ? { ...it, [key]: value } : it)));
  };
  const removeItem = (index: number) => onChange(items.filter((_, i) => i !== index));
  const addItem = () => onChange([...items, { ...empty }]);

  return (
    <div className="space-y-3">
      {items.map((item, index) => (
        <div key={index} className="border border-border-subtle rounded-lg p-3 flex gap-3">
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {fields.map((f) => (
              <div key={String(f.key)} className={f.type === 'textarea' ? 'sm:col-span-2' : ''}>
                {f.type === 'checkbox' ? (
                  <label className="flex items-center gap-2 text-[12px] text-ink-faint">
                    <input type="checkbox" checked={Boolean(item[f.key])} onChange={(e) => updateItem(index, f.key, e.target.checked)} />
                    {f.label}
                  </label>
                ) : f.type === 'textarea' ? (
                  <Textarea value={String(item[f.key] ?? '')} onChange={(e) => updateItem(index, f.key, e.target.value)} placeholder={f.placeholder ?? f.label} rows={2} />
                ) : (
                  <Input
                    type={f.type === 'number' ? 'number' : 'text'}
                    value={String(item[f.key] ?? '')}
                    onChange={(e) => updateItem(index, f.key, f.type === 'number' ? Number(e.target.value) : e.target.value)}
                    placeholder={f.placeholder ?? f.label}
                  />
                )}
              </div>
            ))}
          </div>
          <button onClick={() => removeItem(index)} className="w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-lg hover:bg-error-bg text-error cursor-pointer">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
      <Button type="button" size="sm" variant="secondary" onClick={addItem}>
        <Plus className="w-3.5 h-3.5" /> {addLabel}
      </Button>
    </div>
  );
}

function linesToArray(text: string): string[] {
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
}

export default function AdminProductDetail() {
  const { sku } = useParams<{ sku: string }>();
  const navigate = useNavigate();
  const { push } = useToast();

  const [core, setCore] = useState<CoreProductInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  const [slug, setSlug] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [image, setImage] = useState('');
  const [galleryImages, setGalleryImages] = useState<string[]>([]);
  const [imageAlt, setImageAlt] = useState('');
  const [skinTypesText, setSkinTypesText] = useState('');
  const [howToUseText, setHowToUseText] = useState('');
  const [claimsText, setClaimsText] = useState('');
  const [inci, setInci] = useState('');
  const [description, setDescription] = useState('');
  const [keyIngredients, setKeyIngredients] = useState<{ name: string; benefit: string }[]>([]);
  const [ingredientDetails, setIngredientDetails] = useState<IngredientDetail[]>([]);
  const [reviews, setReviews] = useState<ProductReview[]>([]);
  const [faqs, setFaqs] = useState<FaqItem[]>([]);
  const [skinGuide, setSkinGuide] = useState<{ type: string; tip: string }[]>([]);
  const [clinicalResults, setClinicalResults] = useState<ClinicalResult[]>([]);
  const [videoUrl, setVideoUrl] = useState('');
  const [designV2, setDesignV2] = useState<ProductDesignV2>({});
  // design_v2 only exists once its migration is applied; never send it before.
  const [hasDesignV2, setHasDesignV2] = useState(false);
  // Show the editor for the live storefront design (Website > Settings > Design).
  const siteDesign = useSiteContentValue('site.design', 'en', 'classic');
  const isNewDesign = siteDesign !== 'classic';

  const load = useCallback(async () => {
    if (!sku) return;
    setLoading(true);
    const [coreRes, contentRes] = await Promise.all([
      supabase.from('rxk_public_products_v').select('*').eq('sku', sku).maybeSingle(),
      supabase.from('rxk_product_content').select('*').eq('sku', sku).maybeSingle(),
    ]);
    setCore(coreRes.data as CoreProductInfo | null);
    const c = contentRes.data as Record<string, unknown> | null;
    setSlug((c?.slug as string) ?? '');
    setSubtitle((c?.subtitle as string) ?? '');
    setImage((c?.image as string) ?? '');
    setGalleryImages((c?.gallery_images as string[]) ?? []);
    setImageAlt((c?.image_alt as string) ?? '');
    setSkinTypesText(((c?.skin_types as string[]) ?? []).join('\n'));
    setHowToUseText(((c?.how_to_use as string[]) ?? []).join('\n'));
    setClaimsText(((c?.claims as string[]) ?? []).join('\n'));
    setInci((c?.inci as string) ?? '');
    setDescription((c?.description as string) ?? '');
    setKeyIngredients((c?.key_ingredients as { name: string; benefit: string }[]) ?? []);
    setIngredientDetails((c?.ingredient_details as IngredientDetail[]) ?? []);
    setReviews((c?.reviews as ProductReview[]) ?? []);
    setFaqs((c?.faqs as FaqItem[]) ?? []);
    setSkinGuide((c?.skin_guide as { type: string; tip: string }[]) ?? []);
    setClinicalResults((c?.clinical_results as ClinicalResult[]) ?? []);
    setVideoUrl((c?.video_url as string) ?? '');
    setHasDesignV2(!!c && 'design_v2' in c);
    setDesignV2((c?.design_v2 as ProductDesignV2) ?? {});
    setLoading(false);
  }, [sku]);

  useEffect(() => {
    load();
  }, [load]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingImage(true);
    try {
      const url = await uploadPageImage(file);
      setImage(url);
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleGalleryUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingImage(true);
    try {
      const url = await uploadPageImage(file);
      setGalleryImages((prev) => [...prev, url]);
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSave = async () => {
    if (!sku) return;
    if (!slug.trim()) {
      push('error', 'URL slug is required');
      return;
    }
    setSaving(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const { error } = await supabase.from('rxk_product_content').upsert(
        {
          sku,
          slug: slugify(slug),
          subtitle: subtitle || null,
          image: image || null,
          gallery_images: galleryImages,
          image_alt: imageAlt || null,
          skin_types: linesToArray(skinTypesText),
          how_to_use: linesToArray(howToUseText),
          claims: linesToArray(claimsText),
          inci: inci || null,
          description: description || null,
          key_ingredients: keyIngredients,
          ingredient_details: ingredientDetails,
          reviews,
          faqs,
          skin_guide: skinGuide,
          clinical_results: clinicalResults.length > 0 ? clinicalResults : null,
          video_url: videoUrl || null,
          ...(hasDesignV2 ? { design_v2: designV2 } : {}),
          updated_by: user?.id ?? null,
        },
        { onConflict: 'sku' },
      );
      if (error) throw error;
      push('success', 'Product content saved');
      await load();
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to save product content');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
        <Spinner />
      </AppShell>
    );
  }

  if (!core) {
    return (
      <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
        <EmptyState title="SKU not found in Core ERP" />
      </AppShell>
    );
  }

  return (
    <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
      <div className="space-y-5">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/admin/products')}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-muted text-ink-faint cursor-pointer"
            aria-label="Back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-[20px] font-bold text-ink flex-1 truncate">{core.name_en}</h1>
          {slug && (
            <a href={`/products/${slug}`} target="_blank" rel="noopener noreferrer" className="text-ink-faint hover:text-ink">
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>

        <Card>
          <CardHeader title="Core ERP (read-only)" subtitle="Edit pricing/name/status in Control Core → Inventory" />
          <CardBody>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[12.5px]">
              <div>
                <span className="text-ink-faint">SKU</span>
                <p className="font-medium text-ink">{core.sku}</p>
              </div>
              <div>
                <span className="text-ink-faint">Step</span>
                <p className="font-medium text-ink">{core.step_label}</p>
              </div>
              <div>
                <span className="text-ink-faint">Volume</span>
                <p className="font-medium text-ink">{core.volume_ml ? `${core.volume_ml}ml` : '—'}</p>
              </div>
              <div>
                <span className="text-ink-faint">Price</span>
                <p className="font-medium text-ink">৳{Number(core.msrp_bdt).toLocaleString()}</p>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Storefront Content" />
          <CardBody className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="URL slug (renders at /products/slug)">
                <Input value={slug} onChange={(e) => setSlug(e.target.value)} />
              </Field>
              <Field label="Subtitle">
                <Input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} placeholder="Prep. Brighten. Boost." />
              </Field>
            </div>
            <Field label="Description">
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
            </Field>
            <Field label="Main image">
              <div className="flex items-center gap-3">
                {image && <img src={image} alt="" className="h-16 w-16 object-contain bg-surface-muted rounded-lg" />}
                <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={handleImageUpload} />
                <Button type="button" size="sm" variant="secondary" onClick={() => fileInputRef.current?.click()} disabled={uploadingImage}>
                  <Upload className="w-3.5 h-3.5" /> {uploadingImage ? 'Uploading…' : image ? 'Replace' : 'Upload'}
                </Button>
              </div>
              <p className="text-[11px] text-ink-faint mt-1.5">
                Upload a perfectly square photo (e.g. 1000×1000px) with the product centered on a plain background — every image box on the site is square too, so a square photo
                fills it edge-to-edge with nothing cropped. A non-square photo will have its longer side cropped to fit.
              </p>
            </Field>
            <Field label="Image alt text">
              <Input value={imageAlt} onChange={(e) => setImageAlt(e.target.value)} />
            </Field>
            <Field label="Gallery images">
              <div className="flex flex-wrap items-center gap-2">
                {galleryImages.map((url, i) => (
                  <div key={i} className="relative">
                    <img src={url} alt="" className="h-14 w-14 object-contain bg-surface-muted rounded-lg" />
                    <button
                      onClick={() => setGalleryImages((prev) => prev.filter((_, idx) => idx !== i))}
                      className="absolute -top-1.5 -right-1.5 w-4.5 h-4.5 flex items-center justify-center rounded-full bg-error text-white cursor-pointer"
                      aria-label="Remove"
                    >
                      <Trash2 className="w-2.5 h-2.5" />
                    </button>
                  </div>
                ))}
                <input ref={galleryInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={handleGalleryUpload} />
                <Button type="button" size="sm" variant="secondary" onClick={() => galleryInputRef.current?.click()} disabled={uploadingImage}>
                  <Plus className="w-3.5 h-3.5" /> Add
                </Button>
              </div>
              <p className="text-[11px] text-ink-faint mt-1.5">
                Same as the main image — square, product centered, consistent lighting/background across all gallery photos looks best in the thumbnail strip.
              </p>
            </Field>
            {!isNewDesign && (
              <Field label="How-to-use video (optional)">
                <Input value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="https://youtube.com/watch?v=... or a direct .mp4 URL" />
                <p className="text-[11px] text-ink-faint mt-1.5">
                  Leave blank to keep showing "Coming Soon" on the product page. Accepts a YouTube/Vimeo link or a direct video file URL.
                </p>
              </Field>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Skin types (one per line)">
                <Textarea value={skinTypesText} onChange={(e) => setSkinTypesText(e.target.value)} rows={4} />
              </Field>
              <Field label="Claims / badges (one per line)">
                <Textarea value={claimsText} onChange={(e) => setClaimsText(e.target.value)} rows={4} />
              </Field>
            </div>
            <Field label="How to use (one step per line)">
              <Textarea value={howToUseText} onChange={(e) => setHowToUseText(e.target.value)} rows={4} />
            </Field>
            <Field label="Full ingredient list (INCI)">
              <Textarea value={inci} onChange={(e) => setInci(e.target.value)} rows={3} />
            </Field>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Key Ingredients" subtitle="Short highlight tags shown on the product page" />
          <CardBody>
            <RepeaterEditor
              items={keyIngredients}
              onChange={setKeyIngredients}
              empty={{ name: '', benefit: '' }}
              addLabel="Add ingredient"
              fields={[
                { key: 'name', label: 'Name' },
                { key: 'benefit', label: 'Benefit', type: 'textarea' },
              ]}
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Ingredient Details" subtitle="Clinical breakdown cards" />
          <CardBody>
            <RepeaterEditor
              items={ingredientDetails}
              onChange={setIngredientDetails}
              empty={{ name: '', inciFull: '', concentration: '', clinicalRef: '', benefitSummary: '' }}
              addLabel="Add ingredient detail"
              fields={[
                { key: 'name', label: 'Name' },
                { key: 'concentration', label: 'Concentration' },
                { key: 'inciFull', label: 'Full INCI name' },
                { key: 'clinicalRef', label: 'Clinical reference', type: 'textarea' },
                { key: 'benefitSummary', label: 'Benefit summary', type: 'textarea' },
              ]}
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Skin Type Guide" />
          <CardBody>
            <RepeaterEditor
              items={skinGuide}
              onChange={setSkinGuide}
              empty={{ type: '', tip: '' }}
              addLabel="Add skin type tip"
              fields={[
                { key: 'type', label: 'Skin type' },
                { key: 'tip', label: 'Tip', type: 'textarea' },
              ]}
            />
          </CardBody>
        </Card>

        {isNewDesign &&
          (hasDesignV2 ? (
            <DesignV2Editor value={designV2} onChange={setDesignV2} stepKeyword={core.step_label} skinTypes={linesToArray(skinTypesText)} />
          ) : (
            <Card>
              <CardHeader title="New design" subtitle="Apply the design_v2 migration to edit the new product page sections here" />
            </Card>
          ))}

        {!isNewDesign && (
          <>
            <Card>
              <CardHeader title="Clinical Results" subtitle="Optional — leave empty to hide this section" />
              <CardBody>
                <RepeaterEditor
                  items={clinicalResults}
                  onChange={setClinicalResults}
                  empty={{ metric: '', week4: 0, week8: 0, unit: '%' }}
                  addLabel="Add result"
                  fields={[
                    { key: 'metric', label: 'Metric' },
                    { key: 'unit', label: 'Unit' },
                    { key: 'week4', label: 'Week 4', type: 'number' },
                    { key: 'week8', label: 'Week 8', type: 'number' },
                  ]}
                />
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="Reviews" />
              <CardBody>
                <RepeaterEditor
                  items={reviews}
                  onChange={setReviews}
                  empty={{ name: '', skin: '', rating: 5, date: '', text: '', verified: true }}
                  addLabel="Add review"
                  fields={[
                    { key: 'name', label: 'Customer name' },
                    { key: 'skin', label: 'Skin type' },
                    { key: 'rating', label: 'Rating (1-5)', type: 'number' },
                    { key: 'date', label: 'Date' },
                    { key: 'text', label: 'Review text', type: 'textarea' },
                    { key: 'verified', label: 'Verified purchase', type: 'checkbox' },
                  ]}
                />
              </CardBody>
            </Card>
          </>
        )}

        <Card>
          <CardHeader title="FAQs" />
          <CardBody>
            <RepeaterEditor
              items={faqs}
              onChange={setFaqs}
              empty={{ question: '', answer: '' }}
              addLabel="Add FAQ"
              fields={[
                { key: 'question', label: 'Question' },
                { key: 'answer', label: 'Answer', type: 'textarea' },
              ]}
            />
          </CardBody>
        </Card>

        <Button onClick={handleSave} loading={saving}>
          Save
        </Button>
      </div>
    </AppShell>
  );
}
